import assertNever from '@watershed/shared-util/src/assertNever';
import { BuildingSizeUnit } from '@watershed/constants/buildings';
import {
  EmissionsYearSource,
  EstimationMethodology,
  FundAssetClass,
} from '@watershed/constants/finance';

// eslint-disable-next-line no-restricted-imports
import { GQExternalReportType } from '../../generated/graphql-schema-types';
import { formatList } from '@watershed/intl/formatters';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import { isSourceEstimationEngine } from '../utils/assetFootprintSourceUtil';
import type { AssetYearFieldsTemp } from '../types/FinanceFragments';
import {
  getScopeOverrideCoverageForEstimations,
  ASSET_CLASS_HAS_SCOPE_3,
  type ScopeOverrideCoverage,
} from '../utils/scopeOverrideCoverageUtils';
import EmissionsYear from '../../companyData/EmissionsYear';
import { CompanyEstimateJobInput } from '../../types';
import { EEIO_MODEL_KIND_TO_ESTIMATION_METHODOLOGY_MAP } from '../utils/eeioModelKindUtils';

function footprintSourceLabelWithScopeOverrideCoverage(
  scopeOverrideCoverage: ScopeOverrideCoverage
): string {
  switch (scopeOverrideCoverage) {
    case 'only-overrides':
      return 'Overrides';
    case 'partial':
      return 'Estimation (With overrides)';
    case 'none':
      return 'Estimation';
    default:
      assertNever(scopeOverrideCoverage);
  }
}

export function getBuildingSizeUnitFieldUnit(
  buildingSizeUnit: BuildingSizeUnit | null
) {
  switch (buildingSizeUnit) {
    case BuildingSizeUnit.SquareFeet:
      return 'ft²';
    case BuildingSizeUnit.SquareMeters:
      return 'm²';
    case null:
      return null;
  }
}

export function getEmissionsSourceDescriptor({
  emissionsYearSource,
  reportingYear,
  publicDisclosure,
  hasOverrides,
  isArchived,
}: {
  emissionsYearSource: EmissionsYearSource;
  reportingYear: number;
  publicDisclosure:
    | { publishingYear: number; reportType: GQExternalReportType }
    | null
    | undefined;
  /**
   * Only true for estimations that have overrides (i.e. always false for
   * non-estimation)
   */
  hasOverrides: boolean;
  isArchived: boolean;
}) {
  const { publishingYear, reportType } = publicDisclosure ?? {};

  const { label, inParantheses } = (() => {
    switch (emissionsYearSource) {
      case EmissionsYearSource.CompanyEstimatationEngine:
      case EmissionsYearSource.BuildingEstimationEngine:
      case EmissionsYearSource.MotorVehicleEstimationEngine: {
        if (hasOverrides) {
          return {
            label: 'Estimation with overrides',
            inParantheses: [reportingYear.toString()],
          };
        }

        return {
          label: 'Estimation',
          inParantheses: [reportingYear.toString()],
        };
      }
      case EmissionsYearSource.WatershedMeasurement:
        return {
          label: 'Watershed disclosure',
          inParantheses: [reportingYear.toString()],
        };
      case EmissionsYearSource.Survey:
        return {
          label: 'Portal responses',
          inParantheses: [reportingYear.toString()],
        };
      case EmissionsYearSource.PrivateCdp:
        return {
          label: 'Private CDP disclosure',
          inParantheses: [reportingYear.toString()],
        };
      case EmissionsYearSource.ManualEntry:
        return {
          label: 'Manual entry',
          inParantheses: [],
        };
      case EmissionsYearSource.SurveyEstimate:
        return {
          label: 'Portal',
          inParantheses: [reportingYear.toString()],
        };
      case EmissionsYearSource.PublicDisclosure:
        const publicReportLabel = reportType
          ? `${reportType} report`
          : 'Public report';

        if (reportingYear === publishingYear) {
          return {
            label: publicReportLabel,
            inParantheses: [reportingYear.toString()],
          };
        } else if (publishingYear != null) {
          return {
            label: publicReportLabel,
            inParantheses: [
              `reporting year ${reportingYear}`,
              `published ${publishingYear}`,
            ],
          };
        }
        return {
          label: publicReportLabel,
          inParantheses: [`reporting year ${reportingYear}`],
        };
      default:
        assertNever(emissionsYearSource);
    }
  })();

  const descriptor = buildDescriptor(label, [
    ...inParantheses,
    isArchived ? 'archived' : null,
  ]);

  return descriptor;
}

function buildDescriptor(
  description: string,
  inParantheses: Array<string | null>
) {
  const nonNullInParantheses = inParantheses?.filter(isNotNullish);

  if (!nonNullInParantheses || nonNullInParantheses.length === 0) {
    return description;
  }
  return `${description} (${formatList(nonNullInParantheses, { locale: 'en' })})`;
}

/**
 * Maps a versioned estimation methodology to its simplified EEIO model family
 * name ("CEDA" or "USEEIO").
 */
export function getEeioModelFamily(
  methodology: EstimationMethodology
): 'CEDA' | 'USEEIO' {
  switch (methodology) {
    case EstimationMethodology.CEDA:
    case EstimationMethodology.CEDA_V5:
    case EstimationMethodology.CEDA_V7:
      return 'CEDA';
    case EstimationMethodology.USEEIO:
    case EstimationMethodology.USEEIO_2_1:
    case EstimationMethodology.USEEIO_2_2_22:
      return 'USEEIO';
    default:
      assertNever(methodology);
  }
}

/**
 * Computes the EEIO model dimension label for the data explorer.
 *
 * For non-estimation sources, returns "None (Disclosure)". For estimation
 * sources, combines the EEIO model family (CEDA/USEEIO) with override
 * coverage status.
 */
export function getEeioModelLabel({
  scopeOverrideCoverage,
  eeioModelFamily,
  emissionsYearSource,
}: {
  scopeOverrideCoverage: ScopeOverrideCoverage;
  eeioModelFamily: 'CEDA' | 'USEEIO';
  emissionsYearSource: EmissionsYearSource;
}): string {
  if (!isSourceEstimationEngine(emissionsYearSource)) {
    return 'None (Disclosure)';
  }

  switch (scopeOverrideCoverage) {
    case 'only-overrides':
      return 'None (Overrides)';
    case 'partial':
      return `${eeioModelFamily} (With overrides)`;
    case 'none':
      return eeioModelFamily;
    default:
      assertNever(scopeOverrideCoverage);
  }
}

export function getFootprintSourceLabel(
  assetYearFields: AssetYearFieldsTemp,
  assetClass: FundAssetClass,
  emissionsYear: EmissionsYear | null,
  existingDescriptor: string | null,
  usedPcaf5?: boolean
) {
  const emissionsYearSource = emissionsYear?.emissionsYearSource ?? null;
  if (
    emissionsYearSource == null ||
    !isSourceEstimationEngine(emissionsYearSource)
  ) {
    return existingDescriptor;
  }

  // For PCAF 5, we skip the override coverage logic and just return "Estimation"
  // because override state is not relevant for holding-level estimations
  if (usedPcaf5) {
    return 'Estimation';
  }

  const scopeOverrideCoverage = getScopeOverrideCoverageForEstimations({
    fields: assetYearFields,
    includesScope3: ASSET_CLASS_HAS_SCOPE_3[assetClass],
  });

  return footprintSourceLabelWithScopeOverrideCoverage(scopeOverrideCoverage);
}

export function getEeioModelFamilyFromEmissionsYear(
  emissionsYear: EmissionsYear | null
) {
  let estimationMethodology: EstimationMethodology | null = null;
  const footprintEstimationOutput = emissionsYear?.footprintEstimateOutput;
  if (isNotNullish(footprintEstimationOutput)) {
    const eeioModelKind = (
      footprintEstimationOutput.modelInput as unknown as CompanyEstimateJobInput
    )?.eeioModelKind;
    estimationMethodology = eeioModelKind
      ? EEIO_MODEL_KIND_TO_ESTIMATION_METHODOLOGY_MAP[eeioModelKind]
      : null;
  }
  const eeioModelFamily = estimationMethodology
    ? getEeioModelFamily(estimationMethodology)
    : null;
  return eeioModelFamily;
}

export function getEstimationMethodologyLabel(
  assetYearFields: AssetYearFieldsTemp,
  assetClass: FundAssetClass,
  emissionsYear: EmissionsYear | null,
  usedPcaf5: boolean
) {
  const eeioModelFamily = getEeioModelFamilyFromEmissionsYear(emissionsYear);

  // For PCAF 5:
  // - For non-CLOI holding, we should display CEDA or EXIOBase, but those information lives in data
  //   lineage and is hard to fetch. So for now, we return null.
  // - For CLOI holdings, we do have the information of EEIO model (CEDA or USEEIO) from the footprint
  //   configuration or custom EEIO model at asset year level, so we return it here.
  // Either way, we skip the override coverage logic, because override does not apply to holding-level
  // estimations.
  if (usedPcaf5) {
    if (assetClass === FundAssetClass.CommercialLineOfInsurance) {
      return eeioModelFamily;
    }
    return null;
  }

  // Disclosure
  const emissionsYearSource = emissionsYear?.emissionsYearSource ?? null;
  if (emissionsYearSource == null) {
    return null;
  }
  if (!isSourceEstimationEngine(emissionsYearSource)) {
    return 'None (Disclosure)';
  }

  // Constructing the label from EEIO model and override coverage
  const scopeOverrideCoverage = getScopeOverrideCoverageForEstimations({
    fields: assetYearFields,
    includesScope3: ASSET_CLASS_HAS_SCOPE_3[assetClass],
  });

  return eeioModelFamily != null
    ? getEeioModelLabel({
        scopeOverrideCoverage,
        eeioModelFamily,
        emissionsYearSource,
      })
    : null;
}
